import React from "react";
import { useEffect, useState } from "react";

interface NotificationProps {
  title: string;
  message?: string;
  type?: "success" | "error" | "info";
  onClose?: () => void;
  actions?: React.ReactNode;
  duration?: number; // auto-dismiss time in ms (optional)
}

export default function Notification({
  title,
  message,
  type = "info",
  onClose,
  actions,
  duration = 5000, // default to 5000ms if not provided
}: NotificationProps) {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100); // Progress in percentage (100 means full)

  useEffect(() => {
    if (duration) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress <= 0) {
            clearInterval(interval);
            setVisible(false);
            onClose?.();
            return 0;
          }
          return prevProgress - (100 / (duration / 100)); // Decrease progress over time
        });
      }, 100); // Update every 100ms
      return () => clearInterval(interval); // Cleanup the interval on component unmount
    }
  }, [duration, onClose]);

  if (!visible) return null; // Do not render when not visible

  const colorMap = {
    success: "text-green-500",
    error: "text-red-500",
    info: "text-blue-500",
  };

  return (
    <div className="fixed top-5 right-5 z-50 bg-white shadow-lg border border-gray-200 rounded-lg p-4 flex items-start space-x-4 max-w-sm">
      <div className="flex-shrink-0">
        <svg
          className={`h-6 w-6 ${colorMap[type]}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0Z"
          />
        </svg>
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        {message && <p className="text-sm text-gray-500">{message}</p>}
        {actions && <div className="mt-2 flex space-x-2">{actions}</div>}
      </div>
      <button
        onClick={() => {
          setVisible(false);
          onClose?.();
        }}
        className="text-gray-400 hover:text-gray-600"
      >
        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg">
        <div
          className="h-full bg-blue-500 rounded-b-lg"
          style={{ width: `${progress}%`, transition: "width 0.1s" }}
        ></div>
      </div>
    </div>
  );
}
